import { Router } from '@lightningjs/sdk'

import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import { Registry } from '@lightningjs/sdk'
import SideModal from './components/player/sideModal'
import ToastNotification from './components/player/toastNotification'
import SpeechComp from './components/speechComp/speechComp'
import { appTheme } from './constants/colors'
import {
  UniNeueBold,
  UniNeueBook,
  UniNeueHeavy,
  UniNeueLight,
  UniNeueRegular,
} from './constants/fonts'
import { WIDGETS_Z_INDEX } from './constants/general'
import { Fonts } from './constants/paths'
import { createBootRoute, getRoutes } from './lib/routes'
import { AppConfig } from './services/appConfig'
import { getAsset } from './utils/image'
import { isLowEndDevice } from './utils/info'
import { captureAppOpened, captureSessionData } from './utils/segmentAnalytics'
import { setDevSettings } from './utils/settingsUtils'
import { addOverflowHiddenToBody, disableBrowserScroll } from './utils/viewport'
import {
  AuthRequired,
  ChangePassowrd,
  ExitWindow,
  ForceCloseWindow,
  Loading,
  Logout,
  Menu,
  Modal,
} from './widgets'

export default class App extends Router.App {
  static getFonts() {
    return [
      {
        family: UniNeueBold,
        url: getAsset(Fonts.UNI_NEUE_BOLD),
      },
      {
        family: UniNeueBook,
        url: getAsset(Fonts.UNI_NEUE_BOOK),
      },
      {
        family: UniNeueLight,
        url: getAsset(Fonts.UNI_NEUE_LIGHT),
      },
      {
        family: UniNeueHeavy,
        url: getAsset(Fonts.UNI_NEUE_HEAVY),
      },
      {
        family: UniNeueRegular,
        url: getAsset(Fonts.UNI_NEUE_REGULAR),
      },
    ]
  }

  static colors() {
    return appTheme
  }

  _setup() {
    disableBrowserScroll()
    addOverflowHiddenToBody()
    setDevSettings()
    ;(async () => {
      await AppConfig.setup()
      await SpeechComp.init()

      const routes = getRoutes()

      const bootRoute = createBootRoute()
      routes.routes = [...routes.routes, bootRoute]

      Router.startRouter(routes, this)
      await captureSessionData()
      void captureAppOpened()
    })()
  }

  _init() {
    Registry.addEventListener(document, 'cursorStateChange', this.onCursorStateChange)

    if (isWebos && isLowEndDevice()) {
      this.lgMagicRemoteOverrides()
    }
  }

  _disable() {
    Registry.removeEventListener(document, 'cursorStateChange', this.onCursorStateChange)
  }

  onCursorStateChange(event) {
    const activePageComponent = Router.getActivePage() as any

    if (event.detail?.visibility) {
      AppConfig.shared.appSettings.isCursorVisible = true
      activePageComponent?._onCursorEnter?.()
    } else {
      AppConfig.shared.appSettings.isCursorVisible = false
      activePageComponent?._onCursorLeave?.()
    }
  }

  /**
   * Overrides certain core Lightning getters and event handlers related to mouse functionality so
   * it can work on low end devices who need to have the canvas resized to 720p, but need to be displayed
   * on a 1080p screen
   */

  lgMagicRemoteOverrides() {
    /**
     * Step 1: Change the devicePixelRatio from the stage options to 0.66667, the same as this.stage._options.precision,
     * which was set before app launch, inside the renderCanvasAt720p() function. This is because, on the mouse related
     * functions inside Lightning, the coordinates returned by the mouse events are then multiplied by a "precision" factor,
     * which is obtained from the following operation:
     *
     * const precision = this.stage.getRenderPrecision() / this.stage.getOption("devicePixelRatio");
     *
     * We need to have a precision of 1, hence why we need the devicePixelRatio to be the same as the appSettigs precision.
     * We cannot set the devicePixelRatio before the app launches, otherwise, it affects the whole canvas and rendering during the app's
     * setup phase. However, once launched, changing this value is safe, as no other place uses it, except for the mouse coordinates calculations.
     */

    this.stage._options.devicePixelRatio = 0.66667

    /**
     * Step 2: Remove the hover, click and scroll wheel handlers originally set by Lightning. Fortunately, methods to remove them
     * already exist and are publically available from within the stage object.
     */
    this.stage.platform._removeClickHandler()
    this.stage.platform._removeHoverHandler()
    this.stage.platform._removeScrollWheelHandler()

    /**
     * Step 3: Replace the hover, click and scroll wheel handlers with new ones. We keep using the original handlers, also
     * publicly available from the stage object. However, this time we set a flag before the handlers will get called and
     * after they are finished calling, augmenting the stage object with a custom defined flag, returnViewportDimensions. This
     * flag will be used when getting the width (w) and height (h) of the stage object. That way, any other place that tries
     * to retrieve the w and h of the stage object should still keep getting its real values.
     *
     * The reason why the original methods to determine the coordinates for the mouse events were not working is because inside _receiveHover(),
     * _receiveWheel() and _recieveScrollWheel() were only accepting coordinates up to the canvas' width and height html attributes, but not anything
     * beyond that. So when the canvas gets strecthed to 1920px x 1080px with css, anything beyond x = 1280 or y = 720 was not triggering the logic
     * to determine if a Lightning element was being hovered or clicked on. An example of such code is as follows:
     *
     * if (clientX <= this.stage.w && clientY <= this.stage.h) {
     *  this.stage.application.fireBottomUpClickHandler(obj);
     * }
     */

    this.stage.platform.registerClickHandler((e) => {
      this.stage.returnViewportDimensions = true
      this.application._receiveClick(e)
      this.stage.returnViewportDimensions = false
    })

    this.stage.platform.registerHoverHandler((e) => {
      this.stage.returnViewportDimensions = true
      this.application._receiveHover(e)
      this.stage.returnViewportDimensions = false
    })

    this.stage.platform.registerScrollWheelHandler((e) => {
      this.stage.returnViewportDimensions = true
      this.application._recieveScrollWheel(e)
      this.stage.returnViewportDimensions = false
    })

    /**
     * Step 4: Replace the stage object's getters for w and h. Originally, they simply returned the w and h properties
     * within the _options object in the stage object. During the setup logic, in the Launch method, we are already changing these values
     * through the appSettings object. So with this override we let them keep doing what the original getters already do, with the exception
     * that now we check our custom defined returnViewportDimensions flag before returning any value, since it's already available in the stage
     * object. If true, we now return the viewport's width and height, instead of the canvas'. This allows the collision detection logic beyond
     * the canvas' html width and height attributes. Basically, what we would now be doing is the following, while being as least intrusive in the
     * original code as possible:
     *
     * if (clientX <= window.innerWidth && clientY <= window.innerHeight) {
     *  this.stage.application.fireBottomUpClickHandler(obj);
     * }
     */
    Object.defineProperty(this.stage, 'w', {
      get() {
        return this.returnViewportDimensions ? window.innerWidth : this._options.w
      },
    })

    Object.defineProperty(this.stage, 'h', {
      get() {
        return this.returnViewportDimensions ? window.innerHeight : this._options.h
      },
    })
  }

  static _template() {
    return {
      ...super._template(),
      collision: true, // DO NOT REMOVE, otherwise mouse support gets broken
      Widgets: {
        Menu: {
          type: Menu,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        Loading: {
          type: Loading,
          zIndex: WIDGETS_Z_INDEX - 1,
          visible: false,
        },
        ExitWindow: {
          type: ExitWindow,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        Logout: {
          type: Logout,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        Modal: {
          type: Modal,
          zIndex: WIDGETS_Z_INDEX - 1,
          visible: false,
        },
        SideModal: {
          type: SideModal,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        ToastNotification: {
          type: ToastNotification,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        ChangePassword: {
          type: ChangePassowrd,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        ForceCloseWindow: {
          type: ForceCloseWindow,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
        AuthRequired: {
          type: AuthRequired,
          zIndex: WIDGETS_Z_INDEX,
          visible: false,
        },
      },
    }
  }
}
